import React, { useState, useEffect } from 'react';
import { Mail, Search, MessageSquare, RefreshCw, TrendingUp, Globe2, Boxes, Tag, Download, Layers, Briefcase } from 'lucide-react';
import { getAnalyticsOverview, getAnalyticsTopCountries, getAnalyticsMostRequestedProducts, getAnalyticsTopBrands, getAnalyticsTopModels, getAnalyticsBuyerSegments } from '../../lib/api';
import { Toaster, toast } from 'sonner';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'total', label: 'Total' },
];

const CHANNELS = [
  { key: 'subscribers', label: 'Weekly Inventory Signups', icon: Mail, accent: 'text-[#00D4FF]', bgAccent: 'bg-[#00D4FF]/10' },
  { key: 'sourcing_requests', label: 'Custom Sourcing Requests', icon: Search, accent: 'text-[#F59E0B]', bgAccent: 'bg-[#F59E0B]/10' },
  { key: 'contacts', label: 'Contact Requests', icon: MessageSquare, accent: 'text-[#10B981]', bgAccent: 'bg-[#10B981]/10' },
];

export const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [countries, setCountries] = useState(null);
  const [products, setProducts] = useState(null);
  const [brands, setBrands] = useState(null);
  const [models, setModels] = useState(null);
  const [segments, setSegments] = useState(null);
  const [periodDays, setPeriodDays] = useState(0); // 0 = all-time
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setRefreshing(true);
    try {
      const [overviewRes, countriesRes, productsRes, brandsRes, modelsRes, segmentsRes] = await Promise.all([
        getAnalyticsOverview(),
        getAnalyticsTopCountries(10),
        getAnalyticsMostRequestedProducts({ days: periodDays, limit: 20 }),
        getAnalyticsTopBrands({ days: periodDays }),
        getAnalyticsTopModels({ days: periodDays, limit: 20 }),
        getAnalyticsBuyerSegments({ days: periodDays }),
      ]);
      setData(overviewRes.data);
      setCountries(countriesRes.data);
      setProducts(productsRes.data);
      setBrands(brandsRes.data);
      setModels(modelsRes.data);
      setSegments(segmentsRes.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(true);
    const interval = setInterval(() => load(false), 60000); // refresh every 1 min
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodDays]);

  const fmtNum = (n) => (n == null ? '\u2014' : Number(n).toLocaleString('en-US'));

  const downloadCSV = (filename, rows, headers) => {
    if (!rows || rows.length === 0) {
      toast.info('No rows to export');
      return;
    }
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportProducts = () => {
    const periodLabel = periodDays === 0 ? 'all-time' : `${periodDays}d`;
    downloadCSV(
      `luxortek-most-requested-products-${periodLabel}-${new Date().toISOString().slice(0, 10)}.csv`,
      (products?.products || []).map((p, i) => [i + 1, p.brand, p.label, p.count, p.percent, (p.top_countries || []).join(' / ')]),
      ['Rank', 'Brand', 'Product', 'Requests', 'Percent', 'Top Countries'],
    );
  };

  const exportBrands = () => {
    const periodLabel = periodDays === 0 ? 'all-time' : `${periodDays}d`;
    downloadCSV(
      `luxortek-top-brands-${periodLabel}-${new Date().toISOString().slice(0, 10)}.csv`,
      (brands?.brands || []).map((b, i) => [i + 1, b.brand, b.count, b.percent]),
      ['Rank', 'Brand', 'Requests', 'Percent'],
    );
  };

  const exportModels = () => {
    const periodLabel = periodDays === 0 ? 'all-time' : `${periodDays}d`;
    downloadCSV(
      `luxortek-top-models-${periodLabel}-${new Date().toISOString().slice(0, 10)}.csv`,
      (models?.models || []).map((m, i) => [i + 1, m.brand || '', m.model, m.count, m.percent, (m.top_countries || []).join(' / '), m.source]),
      ['Rank', 'Brand', 'Model', 'Requests', 'Percent', 'Top Countries', 'Source'],
    );
  };

  const exportSegments = () => {
    const periodLabel = periodDays === 0 ? 'all-time' : `${periodDays}d`;
    downloadCSV(
      `luxortek-buyer-segments-${periodLabel}-${new Date().toISOString().slice(0, 10)}.csv`,
      (segments?.segments || []).map((s, i) => [i + 1, s.segment, s.count, s.percent, (s.top_countries || []).join(' / ')]),
      ['Rank', 'Segment', 'Requests', 'Percent', 'Top Countries'],
    );
  };

  const grandTotal = data
    ? CHANNELS.reduce((sum, c) => sum + (data[c.key]?.total || 0), 0)
    : 0;
  const todayTotal = data
    ? CHANNELS.reduce((sum, c) => sum + (data[c.key]?.today || 0), 0)
    : 0;

  return (
    <div className="px-6 py-4" data-testid="admin-analytics-page">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1" data-testid="analytics-title">
            Conversion Analytics
          </h1>
          <p className="text-white/55 text-sm">
            Lead capture performance across all conversion channels
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="btn-secondary inline-flex items-center gap-2 self-start disabled:opacity-60"
          data-testid="analytics-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Top-line summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className="border border-[#00D4FF]/30 bg-gradient-to-br from-[#00D4FF]/5 via-[#0A0A0A] to-[#0A0A0A] p-5" data-testid="analytics-card-today">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-[#00D4FF]" />
            <span className="text-[#00D4FF] text-xs font-semibold tracking-widest uppercase">
              Leads Today
            </span>
          </div>
          <div className="text-4xl font-bold text-white">{fmtNum(todayTotal)}</div>
        </div>
        <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid="analytics-card-total">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-white/60" />
            <span className="text-white/55 text-xs font-semibold tracking-widest uppercase">
              All-Time Leads
            </span>
          </div>
          <div className="text-4xl font-bold text-white">{fmtNum(grandTotal)}</div>
        </div>
      </div>

      {/* Per-channel breakdown */}
      {loading ? (
        <p className="text-white/50 text-sm">Loading analytics...</p>
      ) : (
        <div className="space-y-4">
          {CHANNELS.map((channel) => {
            const stats = data?.[channel.key] || {};
            return (
              <div
                key={channel.key}
                className="border border-white/10 bg-[#0A0A0A] overflow-hidden"
                data-testid={`analytics-channel-${channel.key}`}
              >
                <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${channel.bgAccent} ${channel.accent}`}>
                    <channel.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-white text-base font-semibold">{channel.label}</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4">
                  {PERIODS.map((p, idx) => (
                    <div
                      key={p.key}
                      className={`px-5 py-5 ${idx > 0 ? 'border-l border-white/5' : ''} ${idx >= 2 ? 'border-t md:border-t-0 border-white/5' : ''}`}
                      data-testid={`analytics-${channel.key}-${p.key}`}
                    >
                      <div className="text-white/50 text-xs font-medium tracking-wider uppercase mb-1.5">
                        {p.label}
                      </div>
                      <div className={`text-3xl font-bold ${p.key === 'total' ? 'text-[#00D4FF]' : 'text-white'}`}>
                        {fmtNum(stats[p.key])}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data?.generated_at && (
        <p className="text-white/30 text-xs mt-6" data-testid="analytics-timestamp">
          Last updated: {new Date(data.generated_at).toLocaleString()}
        </p>
      )}

      {/* Top Countries */}
      {countries && countries.countries && countries.countries.length > 0 && (
        <div className="mt-10 border border-white/10 bg-[#0A0A0A]" data-testid="analytics-top-countries">
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF]">
              <Globe2 className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-white text-base font-semibold">Top Countries</h3>
              <p className="text-white/45 text-xs mt-0.5">
                Combined leads (subscribers + sourcing requests) by country
              </p>
            </div>
            <div className="text-right">
              <div className="text-white/45 text-[10px] uppercase tracking-widest">Total Leads</div>
              <div className="text-[#00D4FF] text-xl font-bold">{fmtNum(countries.grand_total)}</div>
            </div>
          </div>

          <div className="divide-y divide-white/5">
            {countries.countries.map((row, idx) => {
              const pct = countries.grand_total > 0 ? Math.round((row.total / countries.grand_total) * 100) : 0;
              return (
                <div
                  key={row.country}
                  className="px-5 py-3.5 grid grid-cols-12 gap-4 items-center"
                  data-testid={`top-country-row-${idx}`}
                >
                  <div className="col-span-1 text-white/40 text-xs font-mono">#{idx + 1}</div>
                  <div className="col-span-3 text-white font-medium text-sm truncate">
                    {row.country}
                  </div>
                  <div className="col-span-5 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#00D4FF] to-[#00B8E0]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-white/45 text-xs font-mono w-10 text-right">{pct}%</span>
                  </div>
                  <div className="col-span-2 text-right text-white/55 text-xs">
                    <span className="text-[#00D4FF] font-medium">{fmtNum(row.subscribers)}</span> subs
                    <span className="text-white/20 mx-1">/</span>
                    <span className="text-[#F59E0B] font-medium">{fmtNum(row.sourcing_requests)}</span> src
                  </div>
                  <div className="col-span-1 text-right text-white text-base font-semibold">
                    {fmtNum(row.total)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Period filter (applies to Products + Brands sections below) */}
      <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="analytics-period-filter">
        <span className="text-white/55 text-xs uppercase tracking-widest">Sourcing Insights Period:</span>
        <div className="flex border border-white/10">
          {[
            { v: 30, label: 'Last 30 Days' },
            { v: 90, label: 'Last 90 Days' },
            { v: 0, label: 'All Time' },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setPeriodDays(opt.v)}
              className={`px-4 py-2 text-xs font-medium transition-colors ${periodDays === opt.v ? 'bg-[#00D4FF] text-black' : 'bg-[#0A0A0A] text-white/55 hover:text-white'}`}
              data-testid={`period-btn-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Most Requested Products */}
      {products && (
        <div className="mt-4 border border-white/10 bg-[#0A0A0A]" data-testid="analytics-most-requested-products">
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF]">
              <Boxes className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-white text-base font-semibold">Most Requested Products</h3>
              <p className="text-white/45 text-xs mt-0.5">
                Extracted from {fmtNum(products.total_with_extracted_product)} of {fmtNum(products.total_requests_scanned)} sourcing-request comments
              </p>
            </div>
            <button
              onClick={exportProducts}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 hover:border-[#00D4FF]/40 text-white/70 hover:text-[#00D4FF] text-xs transition-colors"
              data-testid="export-products-btn"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
          </div>

          {products.products && products.products.length > 0 ? (
            <div className="divide-y divide-white/5">
              {products.products.map((p, idx) => (
                <div
                  key={`${p.brand}-${p.label}`}
                  className="px-5 py-4 grid grid-cols-12 gap-4 items-center"
                  data-testid={`product-row-${idx}`}
                >
                  <div className="col-span-1 text-white/40 text-xs font-mono">#{idx + 1}</div>
                  <div className="col-span-4">
                    <div className="text-white font-medium text-sm">
                      {p.brand} <span className="text-white/80">{p.label}</span>
                    </div>
                    {p.top_countries && p.top_countries.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {p.top_countries.slice(0, 3).map((c) => (
                          <span
                            key={c}
                            className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-white/5 text-white/55 border border-white/10"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-4 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#00D4FF] to-[#00B8E0]"
                        style={{ width: `${Math.min(100, p.percent)}%` }}
                      />
                    </div>
                    <span className="text-white/45 text-xs font-mono w-12 text-right">{p.percent}%</span>
                  </div>
                  <div className="col-span-2 text-right text-white/65 text-xs">
                    <span className="text-[#00D4FF] font-semibold">{fmtNum(p.count)}</span> requests
                  </div>
                  <div className="col-span-1 text-right text-white text-base font-semibold">
                    {fmtNum(p.count)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-5 py-10 text-center text-white/45 text-sm" data-testid="products-empty">
              No products extracted from sourcing requests in this period.
            </div>
          )}
        </div>
      )}

      {/* Top Requested Brands */}
      {brands && brands.brands && (
        <div className="mt-6 border border-white/10 bg-[#0A0A0A]" data-testid="analytics-top-brands">
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#F59E0B]/10 text-[#F59E0B]">
              <Tag className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-white text-base font-semibold">Top Requested Brands</h3>
              <p className="text-white/45 text-xs mt-0.5">
                {fmtNum(brands.total_requests_scanned)} sourcing requests analyzed
              </p>
            </div>
            <button
              onClick={exportBrands}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 hover:border-[#F59E0B]/40 text-white/70 hover:text-[#F59E0B] text-xs transition-colors"
              data-testid="export-brands-btn"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
          </div>

          <div className="divide-y divide-white/5">
            {brands.brands.map((b, idx) => (
              <div
                key={b.brand}
                className="px-5 py-3.5 grid grid-cols-12 gap-4 items-center"
                data-testid={`brand-row-${idx}`}
              >
                <div className="col-span-1 text-white/40 text-xs font-mono">#{idx + 1}</div>
                <div className="col-span-3 text-white font-medium text-sm">{b.brand}</div>
                <div className="col-span-6 flex items-center gap-3">
                  <div className="flex-1 h-2 bg-white/5 overflow-hidden">
                    <div
                      className={`h-full ${b.brand.startsWith('Other') ? 'bg-white/20' : 'bg-gradient-to-r from-[#F59E0B] to-[#EA9F0A]'}`}
                      style={{ width: `${Math.min(100, b.percent)}%` }}
                    />
                  </div>
                  <span className="text-white/45 text-xs font-mono w-12 text-right">{b.percent}%</span>
                </div>
                <div className="col-span-2 text-right text-white text-base font-semibold">
                  {fmtNum(b.count)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Models Requested */}
      {models && (
        <div className="mt-6 border border-white/10 bg-[#0A0A0A]" data-testid="analytics-top-models">
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#A855F7]/10 text-[#A855F7]">
              <Layers className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-white text-base font-semibold">Top Models Requested</h3>
              <p className="text-white/45 text-xs mt-0.5">
                {fmtNum(models.total_with_extracted_model)} of {fmtNum(models.total_requests_scanned)} requests have an identifiable model
              </p>
            </div>
            <button
              onClick={exportModels}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 hover:border-[#A855F7]/40 text-white/70 hover:text-[#A855F7] text-xs transition-colors"
              data-testid="export-models-btn"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
          </div>
          {models.models && models.models.length > 0 ? (
            <div className="divide-y divide-white/5">
              {models.models.map((m, idx) => (
                <div
                  key={m.model}
                  className="px-5 py-4 grid grid-cols-12 gap-4 items-center"
                  data-testid={`model-row-${idx}`}
                >
                  <div className="col-span-1 text-white/40 text-xs font-mono">#{idx + 1}</div>
                  <div className="col-span-4">
                    <div className="text-white font-medium text-sm">
                      {m.model}
                      {m.source === 'structured' && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-medium bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30 align-middle">
                          STRUCTURED
                        </span>
                      )}
                      {m.source === 'extracted' && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-medium bg-white/5 text-white/45 border border-white/10 align-middle">
                          EXTRACTED
                        </span>
                      )}
                    </div>
                    <div className="text-white/45 text-xs mt-0.5">
                      {m.brand}
                      {m.top_countries && m.top_countries.length > 0 && (
                        <>
                          {' \u2022 '}
                          {m.top_countries.slice(0, 3).join(' / ')}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="col-span-5 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#A855F7] to-[#7E22CE]"
                        style={{ width: `${Math.min(100, m.percent)}%` }}
                      />
                    </div>
                    <span className="text-white/45 text-xs font-mono w-12 text-right">{m.percent}%</span>
                  </div>
                  <div className="col-span-2 text-right text-white text-base font-semibold">
                    {fmtNum(m.count)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-5 py-10 text-center text-white/45 text-sm" data-testid="models-empty">
              No models identified yet. Adding the structured Model field to the sourcing form will improve this report.
            </div>
          )}
        </div>
      )}

      {/* Top Buyer Segments */}
      {segments && (
        <div className="mt-6 border border-white/10 bg-[#0A0A0A]" data-testid="analytics-buyer-segments">
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#10B981]/10 text-[#10B981]">
              <Briefcase className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-white text-base font-semibold">Top Buyer Segments</h3>
              <p className="text-white/45 text-xs mt-0.5">
                {fmtNum(segments.total_with_segment)} of {fmtNum(segments.total_requests_scanned)} requests specified intended use
                {segments.unspecified > 0 && ` \u2022 ${fmtNum(segments.unspecified)} unspecified`}
              </p>
            </div>
            <button
              onClick={exportSegments}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 hover:border-[#10B981]/40 text-white/70 hover:text-[#10B981] text-xs transition-colors"
              data-testid="export-segments-btn"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
          </div>
          {segments.segments && segments.segments.length > 0 ? (
            <div className="divide-y divide-white/5">
              {segments.segments.map((s, idx) => (
                <div
                  key={s.segment}
                  className="px-5 py-4 grid grid-cols-12 gap-4 items-center"
                  data-testid={`segment-row-${idx}`}
                >
                  <div className="col-span-1 text-white/40 text-xs font-mono">#{idx + 1}</div>
                  <div className="col-span-4">
                    <div className="text-white font-medium text-sm">{s.segment}</div>
                    {s.top_countries && s.top_countries.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {s.top_countries.slice(0, 3).map((c) => (
                          <span
                            key={c}
                            className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-white/5 text-white/55 border border-white/10"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-5 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#10B981] to-[#0EA371]"
                        style={{ width: `${Math.min(100, s.percent)}%` }}
                      />
                    </div>
                    <span className="text-white/45 text-xs font-mono w-12 text-right">{s.percent}%</span>
                  </div>
                  <div className="col-span-2 text-right text-white text-base font-semibold">
                    {fmtNum(s.count)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-5 py-10 text-center text-white/45 text-sm" data-testid="segments-empty">
              No buyer segments captured yet. New sourcing requests with the Intended Use field will appear here.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
